import React from 'react';
import { Box, Typography, Button, useTheme, Card, CardContent, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

/**
 * ErrorBoundary component to catch and handle errors in child components
 * Follows the project's design system with MUI theme integration and animations
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to an error reporting service
        console.error("Error caught by ErrorBoundary:", error, errorInfo);
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // You could send this to your error tracking service here
        // Example: sendToErrorTrackingService(error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    }

    handleGoHome = () => {
        window.location.href = '/';
    }

    render() {
        const { hasError, error, errorInfo } = this.state;
        const { children, theme, } = this.props;

        if (hasError) {
            const containerVariants = {
                hidden: { opacity: 0, y: 20 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: 0.6,
                        ease: "easeOut",
                        staggerChildren: 0.1
                    }
                }
            };

            const itemVariants = {
                hidden: { opacity: 0, y: 10 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: 0.4,
                        ease: "easeOut"
                    }
                }
            };

            return (
                <Box
                    component={motion.div}
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '60vh',
                        padding: theme.spacing(4),
                        width: '100%',
                    }}
                >
                    <Card
                        component={motion.div}
                        variants={itemVariants}
                        sx={{
                            maxWidth: 600,
                            width: '100%',
                            boxShadow: theme.shadows[8],
                            borderRadius: theme.spacing(2),
                            overflow: 'hidden',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: theme.spacing(6),
                                textAlign: 'center',
                            }}
                        >
                            {/* Error Icon */}
                            <Box
                                component={motion.div}
                                variants={itemVariants}
                                sx={{
                                    marginBottom: theme.spacing(3),
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.error.light,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ErrorOutlineIcon
                                        sx={{
                                            fontSize: 40,
                                            color: theme.palette.error.main,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Error Title */}
                            <Typography
                                component={motion.h1}
                                variants={itemVariants}
                                variant="h3"
                                sx={{
                                    color: theme.palette.text.primary,
                                    marginBottom: theme.spacing(2),
                                    fontFamily: theme.typography.fontFamily,
                                    fontWeight: theme.typography.fontWeightBold,
                                    fontSize: {
                                        xs: theme.typography.pxToRem(24),
                                        sm: theme.typography.pxToRem(28),
                                        md: theme.typography.pxToRem(32),
                                    },
                                }}
                            >
                                Oops! Something went wrong
                            </Typography>

                            {/* Error Description */}
                            <Typography
                                component={motion.p}
                                variants={itemVariants}
                                variant="body1"
                                sx={{
                                    marginBottom: theme.spacing(4),
                                    color: theme.palette.text.secondary,
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: theme.typography.pxToRem(16),
                                    lineHeight: 1.6,
                                    maxWidth: '80%',
                                    margin: `0 auto ${theme.spacing(4)}px auto`,
                                }}
                            >
                                We apologize for the inconvenience. An unexpected error has occurred. 
                                Please try refreshing the page or return to the homepage.
                            </Typography>

                            {/* Action Buttons */}
                            <Box
                                component={motion.div}
                                variants={itemVariants}
                                sx={{
                                    display: 'flex',
                                    gap: theme.spacing(2),
                                    justifyContent: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                }}
                            >                                <Button
                                    onClick={this.handleReset}
                                    variant="contained"
                                    startIcon={<RefreshIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: theme.spacing(1),
                                        padding: theme.spacing(1.5, 3),
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: theme.typography.fontWeightMedium,
                                        fontSize: theme.typography.pxToRem(16),
                                        minWidth: 140,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-2px) scale(1.05)',
                                            boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    }}
                                >
                                    Try Again
                                </Button>                                <Button
                                    onClick={this.handleGoHome}
                                    variant="outlined"
                                    startIcon={<HomeIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: theme.spacing(1),
                                        padding: theme.spacing(1.5, 3),
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: theme.typography.fontWeightMedium,
                                        fontSize: theme.typography.pxToRem(16),
                                        minWidth: 140,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-2px) scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                            borderColor: theme.palette.primary.main,
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    Go Home
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Development Error Details */}
                    {process.env.NODE_ENV === 'development' && errorInfo && (
                        <Card
                            component={motion.div}
                            variants={itemVariants}
                            sx={{
                                marginTop: theme.spacing(4),
                                maxWidth: 800,
                                width: '100%',
                                backgroundColor: theme.palette.grey[50],
                                border: `1px solid ${theme.palette.grey[200]}`,
                            }}
                        >
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        marginBottom: theme.spacing(2),
                                        color: theme.palette.text.primary,
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: theme.typography.fontWeightMedium,
                                    }}
                                >
                                    Error Details (Development Mode)
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        color: theme.palette.text.secondary,
                                        fontSize: theme.typography.pxToRem(13),
                                        fontFamily: 'monospace',
                                        backgroundColor: theme.palette.grey[100],
                                        padding: theme.spacing(2),
                                        borderRadius: theme.spacing(1),
                                        overflow: 'auto',
                                        maxHeight: 300,
                                    }}
                                >
                                    {error && error.toString()}
                                    {'\n\n'}
                                    {errorInfo.componentStack}
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            );
        }

        // If there's no error, render children normally
        return children;
    }
}

// Wrapper component to inject theme using useTheme hook
const ErrorBoundaryWithTheme = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return <ErrorBoundary {...props} theme={theme} isMobile={isMobile} />;
};

ErrorBoundaryWithTheme.propTypes = {
    children: PropTypes.node.isRequired,
    fallback: PropTypes.node,
    onError: PropTypes.func
};

export default ErrorBoundaryWithTheme;